/**
 * Shared file sync — mirrors org/team/project files to a `shared/`
 * directory in the agent's workspace, organised by scope:
 *
 *   shared/org/<files…>
 *   shared/teams/<scopeId>/<files…>
 *   shared/projects/<scopeId>/<files…>
 *
 * Backed by the agent self-service API (`AgentApiClient.sharedListFiles`,
 * `AgentApiClient.sharedDownloadUrl`).
 */

import { join, dirname, normalize, sep } from "node:path";
import { mkdir, writeFile, unlink, rm } from "node:fs/promises";
import type { AgentApiClient } from "@alfe.ai/agent-api-client";

const MAX_SHARED_FILE_SIZE = 100 * 1024 * 1024; // 100 MB

/** Throw if `resolvedPath` would escape `baseDir`. */
function assertContained(baseDir: string, resolvedPath: string): void {
  const normalizedBase = normalize(baseDir) + sep;
  const normalizedPath = normalize(resolvedPath);
  if (
    !normalizedPath.startsWith(normalizedBase) &&
    normalizedPath !== normalize(baseDir)
  ) {
    throw new Error(`Path traversal blocked: ${resolvedPath} escapes ${baseDir}`);
  }
}

export interface SharedScope {
  scopeType: "team" | "project" | "org";
  scopeId: string;
  name: string;
}

export interface SharedSyncConfig {
  workspacePath: string;
  client: AgentApiClient;
}

interface PluginLogger {
  info(msg: string): void;
  warn(msg: string): void;
  error(msg: string): void;
  debug(msg: string): void;
}

export interface SharedSyncEngine {
  initialize(scopes: SharedScope[]): Promise<void>;
  updateScopes(scopes: SharedScope[]): Promise<void>;
  handleNotification(filePath: string, eventType: "created" | "deleted"): Promise<void>;
  fullSync(): Promise<void>;
  getScopes(): SharedScope[];
}

export function createSharedSyncEngine(
  config: SharedSyncConfig,
  log: PluginLogger,
): SharedSyncEngine {
  let activeScopes: SharedScope[] = [];
  const sharedDir = join(config.workspacePath, "shared");

  function scopeDir(scope: SharedScope): string {
    if (scope.scopeType === "org") return join(sharedDir, "org");
    const plural = scope.scopeType === "team" ? "teams" : "projects";
    return join(sharedDir, plural, scope.scopeId);
  }

  async function downloadFile(
    scope: SharedScope,
    filePath: string,
    localPath: string,
  ): Promise<void> {
    assertContained(scopeDir(scope), localPath);

    const { downloadUrl } = await config.client.sharedDownloadUrl({
      scope: scope.scopeType,
      scopeId: scope.scopeId,
      filePath,
    });

    // Presigned URL → S3 directly (raw fetch is the right tool here).
    const response = await fetch(downloadUrl);
    if (!response.ok) {
      throw new Error(`Download failed: HTTP ${String(response.status)}`);
    }

    const contentLength = parseInt(
      response.headers.get("content-length") ?? "0",
      10,
    );
    if (contentLength > MAX_SHARED_FILE_SIZE) {
      throw new Error(
        `File too large: ${String(contentLength)} bytes exceeds ${String(MAX_SHARED_FILE_SIZE)} limit`,
      );
    }

    const buffer = Buffer.from(await response.arrayBuffer());
    if (buffer.length > MAX_SHARED_FILE_SIZE) {
      throw new Error(`Downloaded file exceeds size limit: ${String(buffer.length)} bytes`);
    }

    await mkdir(dirname(localPath), { recursive: true });
    await writeFile(localPath, buffer);
  }

  async function syncScope(scope: SharedScope): Promise<void> {
    const dir = scopeDir(scope);
    await mkdir(dir, { recursive: true });

    try {
      const { files } = await config.client.sharedListFiles({
        scope: scope.scopeType,
        scopeId: scope.scopeId,
      });

      for (const file of files) {
        const localPath = join(dir, file.filePath);
        try {
          await downloadFile(scope, file.filePath, localPath);
          log.debug(
            `Shared sync: downloaded ${scope.scopeType}/${scope.scopeId}/${file.filePath}`,
          );
        } catch (err: unknown) {
          log.error(
            `Shared sync: failed to download ${file.filePath}: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      }
    } catch (err: unknown) {
      log.error(
        `Shared sync: failed to list files for ${scope.scopeType}/${scope.scopeId}: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  function parseScopedPath(
    filePath: string,
  ): { scope: SharedScope; relativePath: string } | null {
    if (filePath.includes("..")) return null;

    const orgMatch = /^shared\/org\/(.+)$/.exec(filePath);
    if (orgMatch) {
      const scope = activeScopes.find((s) => s.scopeType === "org");
      if (scope) return { scope, relativePath: orgMatch[1] };
    }

    const teamMatch = /^shared\/teams\/([^/]+)\/(.+)$/.exec(filePath);
    if (teamMatch) {
      const scope = activeScopes.find(
        (s) => s.scopeType === "team" && s.scopeId === teamMatch[1],
      );
      if (scope) return { scope, relativePath: teamMatch[2] };
    }

    const projectMatch = /^shared\/projects\/([^/]+)\/(.+)$/.exec(filePath);
    if (projectMatch) {
      const scope = activeScopes.find(
        (s) => s.scopeType === "project" && s.scopeId === projectMatch[1],
      );
      if (scope) return { scope, relativePath: projectMatch[2] };
    }

    return null;
  }

  return {
    async initialize(scopes: SharedScope[]): Promise<void> {
      activeScopes = [...scopes];
      log.info(`Shared sync: initializing with ${String(scopes.length)} scope(s)`);
      await mkdir(sharedDir, { recursive: true });
      for (const scope of scopes) {
        await syncScope(scope);
      }
      log.info("Shared sync: initialization complete");
    },

    async updateScopes(newScopes: SharedScope[]): Promise<void> {
      const oldIds = new Set(activeScopes.map((s) => `${s.scopeType}:${s.scopeId}`));
      const newIds = new Set(newScopes.map((s) => `${s.scopeType}:${s.scopeId}`));

      for (const scope of activeScopes) {
        const key = `${scope.scopeType}:${scope.scopeId}`;
        if (!newIds.has(key)) {
          const dir = scopeDir(scope);
          try {
            await rm(dir, { recursive: true, force: true });
            log.info(`Shared sync: removed scope directory ${dir}`);
          } catch (err: unknown) {
            log.warn(
              `Shared sync: failed to remove ${dir}: ${err instanceof Error ? err.message : String(err)}`,
            );
          }
        }
      }

      for (const scope of newScopes) {
        const key = `${scope.scopeType}:${scope.scopeId}`;
        if (!oldIds.has(key)) {
          log.info(
            `Shared sync: new scope ${scope.scopeType}/${scope.scopeId} — syncing files`,
          );
          await syncScope(scope);
        }
      }

      activeScopes = [...newScopes];
    },

    async handleNotification(
      filePath: string,
      eventType: "created" | "deleted",
    ): Promise<void> {
      const parsed = parseScopedPath(filePath);
      if (!parsed) {
        log.debug(`Shared sync: ignoring notification for unknown path: ${filePath}`);
        return;
      }

      const dir = scopeDir(parsed.scope);
      const localPath = join(dir, parsed.relativePath);

      try {
        assertContained(dir, localPath);
      } catch {
        log.warn(`Shared sync: path traversal blocked for ${filePath}`);
        return;
      }

      if (eventType === "deleted") {
        try {
          await unlink(localPath);
          log.debug(`Shared sync: deleted ${filePath}`);
        } catch {
          // idempotent — file may not exist locally on out-of-order events
        }
        return;
      }

      try {
        await downloadFile(parsed.scope, parsed.relativePath, localPath);
        log.debug(`Shared sync: pulled ${filePath}`);
      } catch (err: unknown) {
        log.error(
          `Shared sync: failed to pull ${filePath}: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    },

    async fullSync(): Promise<void> {
      log.info(`Shared sync: full sync across ${String(activeScopes.length)} scope(s)`);
      for (const scope of activeScopes) {
        await syncScope(scope);
      }
    },

    getScopes(): SharedScope[] {
      return [...activeScopes];
    },
  };
}
